import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Dialog, DialogType, DialogFooter, DefaultButton, MessageBar, MessageBarType, Stack, PrimaryButton, DatePicker, Label } from '@fluentui/react';
import { Provider as ProviderContext } from 'react-redux';
import { store } from '../../../redux/store';
import { useState } from 'react';
import { formatDate } from '../../../util/Date';
import { CreateFileParams, HealAPI } from '../../../healApi';
import { ToastParams } from '../../../redux/message/types';
import BlockUi from 'react-block-ui';

export interface DeceasedPatientDialogProps {
  patientId: string;
  onComplete: () => void;
  toast: (params: ToastParams) => void;
}

export class DeceasedPatientDialog {
  public static show(options: DeceasedPatientDialogProps) {
    const dialogNode = document.createElement('div');
    const close = (): void => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(<DeceasedPatientInternal {...options} close={close} />, dialogNode);
  }
}

const DeceasedPatientInternal = ({
  close,
  patientId,
  onComplete,
  toast,
}: {
  close: () => void;
  patientId: string;
  onComplete: () => void;
  toast: (params: ToastParams) => void;
}) => {
  const [date, setDate] = useState(new Date());
  const fileInput = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File>();
  const [filePreview, setFilePreview] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const CANCEL_REASON_ID = '303';

  async function saveFile() {
    setSaving(true);
    setErrorMessage('');
    if (!file) {
      setErrorMessage('Upload a file first');
      return;
    }

    const createFileParams: CreateFileParams = {
      patientId: patientId,
      contentType: file.type,
      extension: file.name.split('.').pop(),
      documentType: 'deathCertificate',
      releaseToPatient: false,
    };

    // 1. Upload the file to file-manager / associate to the patient
    const createFileResponse = await HealAPI.createFile(createFileParams);

    const fileObject = createFileResponse.data?._items?.[0];
    const fileObjectErrorMessage = fileObject?._error?.message ?? createFileResponse.data?._error?.message;

    if (!fileObject || !!fileObject?._error || !!createFileResponse.data?._error) {
      setErrorMessage(`There was a problem creating patient file, please try again later. (${fileObjectErrorMessage})`);
      setSaving(false);
      return;
    }

    const urlToUploadFile = fileObject.upload_url?.put_url;
    if (!urlToUploadFile) {
      setErrorMessage('Death certificate cannot be uploaded. No upload URL was found. This should not happen. Please contact engineering.');
      HealAPI.deletePatientFile(patientId, fileObject._id, fileObject.context);
      setSaving(false);
      return;
    }

    const uploadFileResponse = await HealAPI.uploadFile(urlToUploadFile, file);
    if (!uploadFileResponse.ok) {
      setErrorMessage(`Death certificate failed to upload. Please try again. If this problem persists please contact engineering. (${uploadFileResponse.data.message})`);
      HealAPI.deletePatientFile(patientId, fileObject._id, fileObject.context);
      setSaving(false);
      return;
    }

    // 2. Mark patient as deceased with the date
    const markPatientDeceasedResponse = await HealAPI.markPatientAsDeceased(patientId, date);
    if (!markPatientDeceasedResponse.ok) {
      setErrorMessage(`Death certificate was uploaded, but failed to mark patient as deceased. Please contact engineering.`);
      setSaving(false);
      return;
    }

    // 3. Cancel all future visits
    const cancelAllVisitsResponse = await HealAPI.cancelAllFutureVisits(patientId, CANCEL_REASON_ID, 'Deceased Patient');
    if (!cancelAllVisitsResponse.ok) {
      setErrorMessage(`Death certificate was uploaded and patient was marked as deceased, but failed to cancel future visits. Please contact engineering.`);
      setSaving(false);
      return;
    }

    toast({
      type: 'success',
      title: 'Success',
      content: 'Patient successfully marked as deceased',
    });
    onComplete();
    close();
  }

  const onSelectFile = (file: File) => {
    setSaving(true);
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onloadend = () => {
      setFilePreview(fileReader.result);
      setFile(file);
      setSaving(false);
    };
  };

  return (
    <ProviderContext store={store}>
      <Dialog
        hidden={false}
        modalProps={{ isBlocking: true }}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Mark patient as deceased',
        }}
        onDismiss={close}
      >
        {errorMessage && <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>}
        <BlockUi tag="div" blocking={saving}>
          <Stack horizontal reversed tokens={{ childrenGap: 12 }}>
            <Stack tokens={{ childrenGap: 12 }}>
              <DatePicker label="Deceased date" isRequired onSelectDate={(v) => setDate(v!)} value={date} allowTextInput={true} formatDate={formatDate} style={{ maxWidth: 500 }} />
              <Stack>
                <Label required>Attach death certificate</Label>
                <input
                  value={undefined}
                  ref={fileInput}
                  type="file"
                  accept="image/*, application/pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files?.[0] && onSelectFile(e.target.files[0])}
                />
                <DefaultButton text="Browse..." onClick={() => fileInput.current && fileInput.current.click()} allowDisabledFocus={true} data-tid="btn_browse" />
              </Stack>
              <MessageBar messageBarType={MessageBarType.severeWarning}>Once this is completed, any future visits will be canceled.</MessageBar>
            </Stack>
            {filePreview && (
              <div
                style={{
                  backgroundImage: (('url(' + filePreview) as string) + ')',
                  left: 110,
                  border: '1px solid #ccc',
                  height: 150,
                  width: 100,
                  marginTop: 9,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                }}
              />
            )}
          </Stack>
          <DialogFooter>
            <PrimaryButton text="Mark as Deceased" onClick={saveFile} disabled={!file || !date} />
            <DefaultButton text="Cancel" onClick={close} />
          </DialogFooter>
        </BlockUi>
      </Dialog>
    </ProviderContext>
  );
};
